import { FilterMatchMode } from 'primereact/api'
import { DataTable, DataTableFilterMeta } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Dialog } from 'primereact/dialog'
import { confirmDialog } from 'primereact/confirmdialog'
import { FC, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import {
  useGetAdmApiSeries,
  useGetAdmApiBaseConfigs,
  useGetAdmApiConfigDetail,
  useImportPressureTapConfig,
  useDeletePtapLocationSet,
  useUploadPressureTapCsv
} from '../api/pressure-tap-import-API'
import { useGetPtapLocationSets } from '../api/ptaplocation-set-API'
import { LoadingIcon, SaveIcon, DownloadIcon, DeleteIcon, UploadIcon } from '../components/Icons'
import { protoService } from '../proto/ProtoService'
import { currentTeamState, currentUserState } from '../utils/state-atoms'
import { canMutate } from '../utils/roles'
import { DropdownStyle } from '../utils/styling-constants'
import IconButton from '../components/buttons/IconButton'
import { getSeriesNameForTeam } from '@aero-platform/shared'

// Interfaces matching the ADMAPI response shapes
interface CfdSeries {
  id: number
  name: string
}

interface CfdBaseConfig {
  id: number
  seriesId: number
  name: string
  description: string
  isDefault: boolean
  isBaseConfig: boolean
  notes: string
}

interface CfdPressureTap {
  id: number
  portNumber: string
  tapId: number
  description: string
  x: number
  y: number
  z: number
  wsiDataChannel: string
  adyDataChannel: string
  cfdDataChannel: string
  isEnabled: boolean
}

interface CfdPressureTapGroup {
  id: number
  name: string
  pressureTaps: CfdPressureTap[]
}

interface CfdConfigDetail {
  id: number
  name: string
  description: string
  pressureTapConfigurationGroups: CfdPressureTapGroup[]
}

const defaultImportedFilters: DataTableFilterMeta = {
  name: { value: null, matchMode: FilterMatchMode.CONTAINS }
}

const importedSetColumns = [
  {
    header: 'Name',
    field: 'name',
    sortable: true,
    headerStyle: {},
    bodyStyle: {},
    headerClassName: '',
    bodyClassName: 'id-cell'
  },
  {
    header: 'Version',
    field: 'version',
    headerClassName: '',
    bodyClassName: ''
  },
  {
    header: 'Files',
    field: 'ptaplocationStates',
    headerClassName: '',
    bodyClassName: '',
    body: (rowData: any) => {
      const states = rowData.ptaplocationStates || []
      return <span>{states.map((s: any) => s.name).join(', ') || '—'}</span>
    }
  }
]

const PressureTapImport: FC = () => {
  const currentTeam = useRecoilValue(currentTeamState)
  const currentUser = useRecoilValue(currentUserState)
  const userCanMutate = canMutate(currentUser?.role?.roleId)

  // ADMAPI state
  const [{ loading: loadingSeries, data: seriesData }, fetchSeries] = useGetAdmApiSeries()
  const [{ loading: loadingConfigs, data: configsData }, fetchConfigs] =
    useGetAdmApiBaseConfigs() as any
  const [{ loading: loadingDetail, data: detailData }, fetchConfigDetail] =
    useGetAdmApiConfigDetail() as any
  const [{ loading: importing }, importConfig] = useImportPressureTapConfig() as any
  const [{ loading: deleting }, deletePtapLocationSet] = useDeletePtapLocationSet()
  const [{ loading: uploading }, uploadPressureTapCsv] = useUploadPressureTapCsv() as any

  // Existing PtapLocationSets
  const [{ loading: loadingImported, data: importedData }, fetchImported] = useGetPtapLocationSets()

  // Local state
  const [seriesList, setSeriesList] = useState<CfdSeries[]>([])
  const [selectedSeries, setSelectedSeries] = useState<CfdSeries | null>(null)
  const [baseConfigs, setBaseConfigs] = useState<CfdBaseConfig[]>([])
  const [selectedConfig, setSelectedConfig] = useState<CfdBaseConfig | null>(null)
  const [importedSets, setImportedSets] = useState<any[]>([])

  // Import form
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [importName, setImportName] = useState('')
  const [importVersion, setImportVersion] = useState('')
  const [importFileName, setImportFileName] = useState('')

  // Preview state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false)
  const [previewConfig, setPreviewConfig] = useState<CfdConfigDetail | null>(null)
  const [previewTaps, setPreviewTaps] = useState<CfdPressureTap[]>([])

  // Upload CSV state
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [uploadName, setUploadName] = useState('')
  const [uploadVersion, setUploadVersion] = useState('')
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState('')
  // Fetch series on mount
  useEffect(() => {
    fetchSeries().catch(err => {
      console.error('Error fetching series:', err)
      toast.error('Failed to load series from ADMAPI.')
    })
  }, [])

  // Fetch imported PtapLocationSets
  useEffect(() => {
    if (currentTeam) {
      fetchImported('').catch(err => {
        console.error('Error fetching imported sets:', err)
      })
    }
  }, [currentTeam])

  // Auto-resolve series from current team
  useEffect(() => {
    if (seriesData && Array.isArray(seriesData) && currentTeam) {
      setSeriesList(seriesData)
      const mappedName = getSeriesNameForTeam(currentTeam)
      if (mappedName) {
        const match = seriesData.find(
          (s: CfdSeries) => s.name.toLowerCase() === mappedName.toLowerCase()
        )
        if (match) {
          setSelectedSeries(match)
        } else {
          setSelectedSeries(null)
        }
      } else {
        setSelectedSeries(null)
      }
    } else {
      setSeriesList([])
      setSelectedSeries(null)
    }
  }, [seriesData, currentTeam])

  // Fetch base configs when series changes
  useEffect(() => {
    if (selectedSeries) {
      setSelectedConfig(null)
      setBaseConfigs([])
      fetchConfigs(selectedSeries.id).catch(err => {
        console.error('Error fetching configs:', err)
        toast.error('Failed to load configs from ADMAPI.')
      })
    }
  }, [selectedSeries])

  // Process configs data
  useEffect(() => {
    if (configsData && Array.isArray(configsData)) {
      setBaseConfigs(configsData)
    } else {
      setBaseConfigs([])
    }
  }, [configsData])

  // Fetch config detail when user selects a config
  useEffect(() => {
    if (selectedConfig) {
      setPreviewTaps([])
      setPreviewConfig(null)
      fetchConfigDetail(selectedConfig.id).catch(err => {
        console.error('Error fetching config detail:', err)
        toast.error('Failed to load config detail from ADMAPI.')
      })
    } else {
      setPreviewTaps([])
      setPreviewConfig(null)
    }
  }, [selectedConfig])

  // Process config detail into preview taps
  useEffect(() => {
    if (detailData && detailData.pressureTapConfigurationGroups) {
      setPreviewConfig(detailData as CfdConfigDetail)
      const taps: CfdPressureTap[] = []
      for (const group of detailData.pressureTapConfigurationGroups) {
        for (const tap of group.pressureTaps || []) {
          if (tap.isEnabled) {
            taps.push(tap)
          }
        }
      }
      taps.sort((a, b) => a.tapId - b.tapId)
      setPreviewTaps(taps)
    } else {
      setPreviewConfig(null)
      setPreviewTaps([])
    }
  }, [detailData])

  // Process imported sets
  useEffect(() => {
    try {
      if (importedData) {
        const tmpList = importedData.protoBytesList?.map((item: string) => {
          return protoService.deSerialize(item, 'ptaplocationset')
        })
        tmpList && setImportedSets(tmpList)
      } else {
        setImportedSets([])
      }
    } catch (error) {
      console.error('Error processing imported sets:', error)
      setImportedSets([])
    }
  }, [importedData])

  const handleDeleteSet = async (ptapSet: any) => {
    if (!ptapSet) return
    confirmDialog({
      message: `Are you sure you want to delete the pressure tap set: '${ptapSet.name}'?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'reject',
      rejectClassName: 'bg-gray-500 text-white',
      accept: async () => {
        try {
          await deletePtapLocationSet({ id: ptapSet._id } as any)
          setImportedSets(prev => prev.filter(item => item._id !== ptapSet._id))
          toast.success('Pressure tap set deleted.')
        } catch (err) {
          console.error(err)
          toast.error('Error deleting pressure tap set.')
        }
      }
    })
  }

  const handleImportClick = () => {
    if (!selectedSeries || !selectedConfig) {
      toast.error('Please select a configuration first.')
      return
    }
    // Pre-fill form defaults
    const safeName = selectedConfig.name.replace(/[^a-zA-Z0-9_\-]/g, '_')
    setImportName(currentTeam || '')
    setImportVersion(selectedConfig.name)
    setImportFileName(safeName)
    setShowImportDialog(true)
  }

  const handleImportSubmit = async () => {
    if (!selectedSeries || !selectedConfig) return
    if (!importVersion) {
      toast.error('Version is required.')
      return
    }
    const fullFileName = importFileName ? `${importFileName}_PTapLoc.csv` : undefined
    try {
      await importConfig({
        seriesId: selectedSeries.id,
        configId: selectedConfig.id,
        body: {
          name: importName,
          version: importVersion,
          fileName: fullFileName
        }
      })
      toast.success(`Pressure tap config "${selectedConfig.name}" imported successfully!`)
      setShowImportDialog(false)
      // Refresh imported sets
      fetchImported('').catch(err => console.error(err))
    } catch (error: any) {
      console.error('Import failed:', error)
      const status = error?.response?.status
      const msg = error?.response?.data || error?.message || 'Unknown error'
      if (status === 409) {
        toast.error(`${msg}`)
      } else {
        toast.error(`Import failed: ${msg}`)
      }
    }
  }

  const validateCsvContent = (content: string): string | null => {
    const lines = content.trim().split(/\r?\n/)
    if (lines.length === 0) {
      return 'CSV file is empty.'
    }
    if (lines.length > 10000) {
      return 'CSV file exceeds maximum of 10,000 rows.'
    }
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue
      const parts = line.split(',')
      if (parts.length !== 4) {
        return `Line ${i + 1}: expected 4 comma-separated values (channel, x, y, z), got ${
          parts.length
        }.`
      }
      const x = parseFloat(parts[1].trim())
      const y = parseFloat(parts[2].trim())
      const z = parseFloat(parts[3].trim())
      if (isNaN(x) || isNaN(y) || isNaN(z)) {
        return `Line ${i + 1}: x, y, z values must be numeric.`
      }
    }
    return null
  }

  const handleUploadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError('')
    const file = e.target.files?.[0]
    if (!file) {
      setUploadFile(null)
      return
    }
    if (!file.name.endsWith('.csv')) {
      setUploadError('Please select a .csv file.')
      setUploadFile(null)
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must not exceed 5 MB.')
      setUploadFile(null)
      return
    }
    setUploadFile(file)
    // Auto-fill name from file name (without extension)
    const nameWithoutExt = file.name.replace(/\.csv$/i, '')
    setUploadName(nameWithoutExt)
  }

  const handleUploadSubmit = async () => {
    if (!uploadFile || !uploadName.trim() || !uploadVersion.trim()) {
      toast.error('Please fill in all required fields and select a file.')
      return
    }

    setUploadError('')

    const csvContent = await uploadFile.text()
    const validationError = validateCsvContent(csvContent)
    if (validationError) {
      setUploadError(validationError)
      return
    }

    try {
      await uploadPressureTapCsv({
        name: uploadName.trim(),
        version: uploadVersion.trim(),
        csvContent,
        fileName: uploadFile.name
      })
      toast.success(`Pressure tap CSV "${uploadFile.name}" uploaded successfully!`)
      setShowUploadDialog(false)
      setUploadFile(null)
      setUploadName('')
      setUploadVersion('')
      setUploadError('')
      // Refresh imported sets
      fetchImported('').catch(err => console.error(err))
    } catch (error: any) {
      console.error('Upload failed:', error)
      const status = error?.response?.status
      const msg = error?.response?.data || error?.message || 'Unknown error'
      if (status === 409) {
        setUploadError(msg)
      } else {
        setUploadError(`Upload failed: ${msg}`)
      }
    }
  }

  return (
    <div className="basis-0 grow flex flex-col items-stretch mt-6 min-w-full space-y-4">
      <div className="flex items-center justify-between pb-4">
        <h1 className="page-title">Pressure Tap Sets</h1>
        <Button
          severity="secondary"
          className="cursor-pointer"
          disabled={uploading}
          onClick={() => {
            setUploadName('')
            setUploadVersion('')
            setUploadFile(null)
            setUploadError('')
            setShowUploadDialog(true)
          }}>
          {uploading ? (
            <LoadingIcon className="h-4 w-4 fill-white animate-spin mr-2" />
          ) : (
            <UploadIcon className="h-4 w-4 fill-white stroke-transparent mr-2" />
          )}
          Upload CSV
        </Button>
      </div>

      <div className="w-full grid grid-cols-4 gap-x-2 gap-y-4 mt-4">
        <div className="flex items-center col-span-3 space-x-2">
          <Dropdown
            filter
            loading={loadingSeries || loadingConfigs}
            filterBy="name"
            filterMatchMode="contains"
            showFilterClear
            disabled={!selectedSeries || currentTeam === 'NBS-HFT'}
            value={selectedConfig}
            emptyMessage={selectedSeries ? 'No Configs Found' : 'No series mapping for this team'}
            pt={{
              ...DropdownStyle,
              root: {
                ...DropdownStyle.root,
                className: 'min-w-[30%] max-w-[50%]'
              }
            }}
            optionLabel="name"
            useOptionAsValue
            options={baseConfigs}
            valueTemplate={option => {
              return <div>{option ? option.name : 'Select Configuration'}</div>
            }}
            itemTemplate={option => {
              return (
                <div className="flex flex-col">
                  <span className="font-medium">{option.name}</span>
                  {option.description && (
                    <span className="text-xs text-gray-500">{option.description}</span>
                  )}
                </div>
              )
            }}
            onChange={e => {
              setSelectedConfig(e.value)
            }}
            placeholder="Select Configuration"
          />
          {selectedConfig && (
            <Button
              severity="secondary"
              className="icon-button cursor-pointer"
              disabled={importing}
              title="Import"
              tooltip="Import"
              tooltipOptions={{ position: 'bottom' }}
              onClick={handleImportClick}>
              {importing ? (
                <LoadingIcon className="h-4 w-4 fill-white animate-spin" />
              ) : (
                <DownloadIcon className="h-4 w-4 fill-white stroke-transparent" />
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Config description + preview */}
      {selectedConfig && (
        <div className="mt-6">
          {selectedConfig.description && (
            <div className="text-lg text-gray-600 dark:text-gray-400 mb-3">
              {selectedConfig.description}
            </div>
          )}
          <DataTable
            value={previewTaps}
            scrollable
            scrollHeight="400px"
            loading={loadingDetail}
            dataKey="id"
            stripedRows
            sortField="tapId"
            sortOrder={1}
            emptyMessage={loadingDetail ? 'Loading...' : 'No enabled taps found.'}
            pt={{
              bodyRow: {
                className: 'table-row'
              }
            }}>
            <Column
              field="description"
              header="Description"
              sortable
              headerClassName="max-w-[250px]"
              bodyClassName="max-w-[250px]"
            />
            <Column
              header="Data Channel"
              sortable
              headerClassName="max-w-[150px]"
              bodyClassName="max-w-[150px]"
              body={(rowData: CfdPressureTap) =>
                rowData.wsiDataChannel || rowData.adyDataChannel || '—'
              }
            />
            <Column
              field="x"
              header="X"
              sortable
              headerClassName="max-w-[120px]"
              bodyClassName="max-w-[120px]"
              body={(rowData: CfdPressureTap) => rowData.x?.toFixed(6) ?? '—'}
            />
            <Column
              field="y"
              header="Y"
              sortable
              headerClassName="max-w-[120px]"
              bodyClassName="max-w-[120px]"
              body={(rowData: CfdPressureTap) => rowData.y?.toFixed(6) ?? '—'}
            />
            <Column
              field="z"
              header="Z"
              sortable
              headerClassName="max-w-[120px]"
              bodyClassName="max-w-[120px]"
              body={(rowData: CfdPressureTap) => rowData.z?.toFixed(6) ?? '—'}
            />
          </DataTable>
        </div>
      )}

      {/* Section 2: Imported PtapLocationSets */}
      <div className="flex text-gm-primary-90 text-xl pt-8 dark:text-light-blue">
        Imported Pressure Tap Location Sets
      </div>
      <section className="col-span-2 flex flex-col items-stretch">
        <DataTable
          value={importedSets}
          scrollable
          scrollHeight="450px"
          loading={loadingImported}
          dataKey="_id"
          stripedRows
          sortField="name"
          emptyMessage="No imported pressure tap sets found."
          pt={{
            bodyRow: {
              className: 'table-row'
            }
          }}>
          {importedSetColumns.map(item => {
            return (
              <Column
                key={item.field}
                field={item.field}
                header={item.header}
                sortable={item.sortable}
                headerStyle={item.headerStyle}
                headerClassName={item.headerClassName}
                bodyStyle={item.bodyStyle}
                bodyClassName={item.bodyClassName}
                body={item.body}
              />
            )
          })}
          <Column
            header="Actions"
            body={(rowData: any) => {
              return (
                <div className="flex items-center space-x-1">
                  <IconButton
                    clear
                    icon={<DeleteIcon className="h-4 w-4 fill-error stroke-transparent" />}
                    title="Delete Pressure Tap Set"
                    onClick={() => handleDeleteSet(rowData)}
                  />
                </div>
              )
            }}
            headerStyle={{ width: '5rem', minWidth: '5rem' }}
            bodyStyle={{ textAlign: 'center', padding: '12px 8px' }}
          />
        </DataTable>
      </section>

      {/* Import Dialog */}
      <Dialog
        header="Import Pressure Tap Configuration"
        visible={showImportDialog}
        closeOnEscape
        pt={{
          root: {
            className: 'w-[500px]'
          },
          mask: {
            className: 'bg-blue-backdrop dark:bg-neutral-80'
          }
        }}
        onHide={() => {
          if (!showImportDialog) return
          setShowImportDialog(false)
        }}
        footer={
          <div className="flex justify-end space-x-2">
            <Button severity="secondary" outlined onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button
              icon={
                importing ? (
                  <LoadingIcon className="h-4 w-4 fill-white animate-spin mr-2" />
                ) : (
                  <SaveIcon className="fill-white w-4 h-4 mr-2" />
                )
              }
              severity="secondary"
              disabled={importing || !importVersion || !userCanMutate}
              onClick={handleImportSubmit}>
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </div>
        }>
        <div className="flex flex-col space-y-4 pt-2">
          <div className="flex flex-col space-y-1">
            <label className="text-sm font-medium">
              Series: <strong>{selectedSeries?.name}</strong>
            </label>
            <label className="text-sm font-medium">
              Config: <strong>{selectedConfig?.name}</strong>
            </label>
            <label className="text-sm font-medium">
              Name:{' '}
              <strong>
                {importVersion}/{importFileName ? `${importFileName}_PTapLoc.csv` : ''}
              </strong>
            </label>
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Name <span className="text-red-500">*</span>
            </label>
            <InputText
              value={importVersion}
              onChange={e => setImportVersion(e.target.value)}
              placeholder="e.g. Cup-2025"
              className="w-full"
            />
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              File Name Prefix (optional)
            </label>
            <div className="flex items-center">
              <InputText
                value={importFileName}
                onChange={e => setImportFileName(e.target.value)}
                placeholder="e.g. MyConfig"
                className="flex-1"
              />
              <span className="ml-2 text-sm font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">
                _PTapLoc.csv
              </span>
            </div>
            <span className="text-xs text-gray-500">
              Leave blank for auto-generated name based on config
            </span>
          </div>
        </div>
      </Dialog>

      {/* Upload CSV Dialog */}
      <Dialog
        header="Upload Pressure Tap CSV"
        visible={showUploadDialog}
        closeOnEscape
        pt={{
          root: {
            className: 'w-[500px]'
          },
          mask: {
            className: 'bg-blue-backdrop dark:bg-neutral-80'
          }
        }}
        onHide={() => {
          if (!showUploadDialog) return
          setShowUploadDialog(false)
        }}
        footer={
          <div className="flex justify-end space-x-2">
            <Button severity="secondary" outlined onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button
              icon={
                uploading ? (
                  <LoadingIcon className="h-4 w-4 fill-white animate-spin mr-2" />
                ) : (
                  <UploadIcon className="fill-white w-4 h-4 mr-2" />
                )
              }
              severity="secondary"
              disabled={
                uploading ||
                !uploadName.trim() ||
                !uploadVersion.trim() ||
                !uploadFile ||
                !userCanMutate
              }
              onClick={handleUploadSubmit}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        }>
        <div className="flex flex-col space-y-4 pt-2">
          <div className="flex flex-col space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Name <span className="text-red-500">*</span>
            </label>
            <InputText
              value={uploadName}
              onChange={e => setUploadName(e.target.value)}
              placeholder="e.g. NBS-HFT-2025"
              className="w-full"
            />
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Version <span className="text-red-500">*</span>
            </label>
            <InputText
              value={uploadVersion}
              onChange={e => setUploadVersion(e.target.value)}
              placeholder="e.g. 2028, Speedway"
              className="w-full"
            />
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              CSV File <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleUploadFileChange}
              className="block w-full text-sm text-gray-700 dark:text-gray-300
                file:mr-4 file:py-2 file:px-4
                file:rounded file:border-0
                file:text-sm file:font-medium
                file:bg-gray-100 file:text-gray-700
                dark:file:bg-neutral-70 dark:file:text-gray-200
                hover:file:bg-gray-200 dark:hover:file:bg-neutral-60
                cursor-pointer"
            />
            <span className="text-xs text-gray-500">
              CSV format: channel, x, y, z (no header row). Max 5 MB.
            </span>
          </div>
          {uploadError && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
              {uploadError}
            </div>
          )}
        </div>
      </Dialog>
    </div>
  )
}

export default PressureTapImport
